import { useEffect } from 'react';
import { getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { getOrInitFirebaseApp } from '@/config/firebaseClient';
import { pushApi } from '@/api/push';

const LS_FCM_TOKEN_KEY = 'sculpx_fcm_token';
const LS_FCM_SYNCED_AT_KEY = 'sculpx_fcm_synced_at';
const FCM_RESYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

function getLastSyncedToken(): { token: string; at: number } | null {
  try {
    const token = localStorage.getItem(LS_FCM_TOKEN_KEY);
    const at = Number(localStorage.getItem(LS_FCM_SYNCED_AT_KEY));
    if (token && Number.isFinite(at) && at > 0) return { token, at };
  } catch { /* ignore */ }
  return null;
}

function markTokenSynced(token: string): void {
  try {
    localStorage.setItem(LS_FCM_TOKEN_KEY, token);
    localStorage.setItem(LS_FCM_SYNCED_AT_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

function isTokenSyncFresh(token: string): boolean {
  const last = getLastSyncedToken();
  if (!last) return false;
  if (last.token !== token) return false;
  return Date.now() - last.at < FCM_RESYNC_INTERVAL_MS;
}

const playNotificationSound = () => {
  const url = import.meta.env.VITE_NOTIFICATION_SOUND_URL;
  if (!url || typeof Audio === 'undefined') return;
  try {
    const audio = new Audio(url);
    audio.volume = 0.4;
    void audio.play().catch(() => {});
  } catch { /* ignore */ }
};

export function useCalendarPushRegistration(accessToken: string | null) {
  useEffect(() => {
    if (!accessToken) return undefined;

    let cancelled = false;
    let unsubForeground: (() => void) | undefined;
    let removeGestureListener: (() => void) | undefined;

    void (async () => {
      const supported = await isSupported();
      if (!supported || cancelled) {
        console.info('[push] Firebase messaging is not supported in this browser');
        return;
      }

      const fb = getOrInitFirebaseApp();
      if (!fb) { console.warn('[push] Firebase app is not configured'); return; }

      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      if (!vapidKey || typeof vapidKey !== 'string') { console.warn('[push] Missing VITE_FIREBASE_VAPID_KEY'); return; }

      const messaging = getMessaging(fb);
      if (typeof Notification === 'undefined') { console.info('[push] Notification API is not available'); return; }

      const registerFcm = async (allowPermissionPrompt: boolean): Promise<'ok' | 'needs-gesture' | 'blocked'> => {
        let permission = Notification.permission;
        if (permission === 'default') {
          if (!allowPermissionPrompt) return 'needs-gesture';
          permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') return 'blocked';
        if (cancelled) return 'blocked';

        let swRegistration: ServiceWorkerRegistration | undefined;
        if ('serviceWorker' in navigator) {
          try { swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js'); }
          catch (err) { console.warn('[push] Service worker registration failed', err); }
        }

        const fcmToken = await getToken(messaging, { vapidKey, ...(swRegistration ? { serviceWorkerRegistration: swRegistration } : {}) });
        if (!fcmToken || cancelled) return 'blocked';

        if (isTokenSyncFresh(fcmToken)) { console.info('[push] FCM token already registered, skipping backend call'); return 'ok'; }

        try {
          await pushApi.registerFcmToken(fcmToken);
          markTokenSynced(fcmToken);
          console.info('[push] FCM token registered with backend');
        } catch {
          console.warn('[push] Failed to register FCM token with backend');
          return 'blocked';
        }
        return 'ok';
      };

      const initial = await registerFcm(false);
      if (initial === 'needs-gesture') {
        const onFirstUserGesture = () => {
          window.removeEventListener('click', onFirstUserGesture, true);
          void registerFcm(true);
        };
        window.addEventListener('click', onFirstUserGesture, { capture: true, once: true });
        removeGestureListener = () => window.removeEventListener('click', onFirstUserGesture, true);
      } else if (initial === 'blocked') {
        console.info('[push] Notification permission blocked/denied');
      }

      if (cancelled) return;

      unsubForeground = onMessage(messaging, (payload) => {
        const t = payload.data?.type;
        if (t !== 'scheduled_reminder' && t !== 'calendar_event_reminder') return;
        playNotificationSound();
        const title = payload.notification?.title ?? 'Calendar';
        const body = payload.notification?.body ?? '';
        if (Notification.permission === 'granted') {
          try { void new Notification(title, { body, icon: '/favicon.ico' }); } catch { /* ignore */ }
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubForeground?.();
      removeGestureListener?.();
    };
  }, [accessToken]);
}
